import { get, post, put, del } from '../apiClient';
import { API_ENDPOINTS, STORAGE_KEYS } from '../config';

const authService = {
  // Login with email and password
  login: async (email, password, rememberMe = false) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
        rememberMe
      });

      if (response && response.responseCode === 200) {
        // Store token and user data
        if (response.data && response.data.token) {
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
        }
        if (response.data && response.data.user) {
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
        }

        if (rememberMe) {
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
        }

        return {
          success: true,
          user: response.data?.user || null,
          token: response.data?.token || null
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Login failed'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  },

  // Login with email OTP
  loginWithEmailOTP: async (email) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.LOGIN_EMAIL_OTP, { email });



      // Check if response is successful (responseCode 200)
      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to send OTP'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  // Verify email OTP
  verifyEmailOTP: async (email, otp) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.VERIFY_EMAIL_OTP, { email, otp });



      if (response.responseCode === 200) {





        // Check if response has token and user data (for login)
        if (response.data.token && response.data.user) {

          // Store token and user data
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));

          return {
            success: true,
            user: response.data.user,
            token: response.data.token
          };
        } else {

          // OTP verified but no login data (for password reset flow)
          return {
            success: true,
            message: response.responseMessage,
            data: response.data
          };
        }
      } else {

        return {
          success: false,
          error: response.responseMessage || 'OTP verification failed'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'OTP verification failed'
      };
    }
  },

  // Login with mobile OTP
  loginMobileOTP: async (mobile) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.LOGIN_MOBILE_OTP, { mobile });



      // Check if response is successful (responseCode 200)
      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to send OTP'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  // Verify mobile OTP
  verifyMobileOTP: async (mobile, otp) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.VERIFY_MOBILE_OTP, { mobile, otp });



      if (response.responseCode === 200) {
        // Check if response has token and user data (for login)
        if (response.data.token && response.data.user) {
          // Store token and user data
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));

          return {
            success: true,
            user: response.data.user,
            token: response.data.token
          };
        } else {
          // OTP verified but no login data (for password reset flow)
          return {
            success: true,
            message: response.responseMessage,
            data: response.data
          };
        }
      } else {
        return {
          success: false,
          error: response.responseMessage || 'OTP verification failed'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'OTP verification failed'
      };
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    try {
      const response = await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
      return response;
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to send reset email'
      };
    }
  },

  // Send OTP for password reset
  sendOTP: async (data) => {
    try {


      let response;
      if (data.method === 'email') {
        response = await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email: data.emailOrPhone });
      } else if (data.method === 'phone') {
        response = await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email: data.emailOrPhone });
      } else {
        throw new Error('Invalid method specified');
      }



      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to send OTP'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to send OTP'
      };
    }
  },

  // Verify OTP for password reset
  verifyOTP: async (data) => {
    try {


      const response = await post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
        email: data.emailOrPhone,
        otp: data.otp
      });



      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'OTP verification failed'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'OTP verification failed'
      };
    }
  },

  // Reset password
  resetPassword: async (data) => {
    try {


      const response = await post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        email: data.emailOrPhone,
        otp: data.otp,
        password: data.newPassword
      });



      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Password reset failed',
          errors: response.errors
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Password reset failed'
      };
    }
  },

  // Logout
  logout: async () => {
    try {
      // Try to call logout API first (with token)
      try {
        await post(API_ENDPOINTS.AUTH.LOGOUT);

      } catch (apiError) {

      }

      // Clear auth data but preserve remember_me preference
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      // Don't clear remember_me flag - preserve user preference


      return { success: true, message: 'Logout successful' };
    } catch (error) {
      // Clear auth data even if something goes wrong, but preserve remember_me
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      // Don't clear remember_me flag - preserve user preference


      return { success: true, message: 'Logout successful' };
    }
  },

  // Get current user
  getCurrentUser: () => {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {

      return null;
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    return !!token;
  },

  // Get auth token
  getAuthToken: () => {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  // Set auth data
  setAuthData: (user, token) => {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  },

  // Clear all auth data
  clearAuthData: () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
  }
};

export default authService;

